import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant =
  | 'primary' // plein, couleur du spot
  | 'soft' // fond pastel, texte couleur du spot
  | 'ghost' // sans fond
  | 'danger' // fond rouge pale, texte rouge
  | 'brand'; // plein cyan, ecrans de marque

/**
 * Bouton de l'application.
 * Les variantes couvrent tous les cas des maquettes ; la couleur suit le
 * spot courant, sauf pour `brand` qui reste cyan.
 */
export function Button({
  variant = 'primary',
  size = 'lg',
  block = false,
  icon,
  loading = false,
  children,
  className = '',
  disabled,
  ...rest
}: {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  /** Occupe toute la largeur disponible. */
  block?: boolean;
  icon?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`btn btn--${variant} btn--${size}${block ? ' btn--block' : ''} ${className}`.trim()}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

/**
 * Bouton rond ne portant qu'une icone : envoyer un message, ouvrir
 * l'agenda, mettre en signet.
 *
 * Il transmet sa ref : les fenetres d'actions sortent du bouton qui les
 * ouvre, et il leur faut son cadre pour savoir ou se poser.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  {
    /** Nom lu par les lecteurs d'ecran. */
    label: string;
    variant?: 'soft' | 'ghost' | 'primary';
    size?: number;
    children: ReactNode;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function IconButton({ label, variant = 'soft', size = 44, children, className = '', ...rest }, ref) {
  return (
    <button
      ref={ref}
      className={`icon-btn icon-btn--${variant} ${className}`.trim()}
      style={{ width: size, height: size }}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <svg className="spinner" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2.4" opacity="0.25" fill="none" />
      <path
        d="M18 10a8 8 0 0 0-8-8"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
